import { getBorder } from 'constants/Style';
import {
  LabeledValue,
  Loader,
  Panel,
  Typography,
} from 'melp-design/components';
import { Colors } from 'melp-design/style';
import { Box, Stack } from '@mui/material';
import moment from 'moment';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import {
  useSurvey,
  useSurveyGroupAssignments,
  useSurveyResults,
} from 'store/surveys';
import { useRouteParams } from 'utils/useIdParam';
import { formatList, formatPercents } from 'utils/format';
import { retriesToAttempts } from 'containers/surveys';
import { usePointsLabel } from 'store/recognitions';
import { SurveyContentSummary } from './content-summary.component';

export const SurveyResults: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: surveyId } = useRouteParams(ROUTES.surveys.survey.details);
  const pointsLabel = usePointsLabel();

  const { data: results, isLoading: isResultsLoading } =
    useSurveyResults(surveyId);
  const { data: survey, isLoading: isSurveyLoading } = useSurvey(surveyId);
  const { data: assignments } = useSurveyGroupAssignments(surveyId);

  if (isResultsLoading || isSurveyLoading) return <Loader />;
  if (!results || !survey) return null;

  return (
    <Stack gap={2}>
      <Panel title={t('sidebar.administratorsInformation')}>
        <Stack gap={4}>
          <Stack gap={2} display="grid" gridTemplateColumns="1fr 1fr">
            <LabeledValue
              label={t('surveys.sentTo')}
              value={formatList(
                i18n.language,
                assignments?.items
                  .filter((assignment) => assignment.assignmentStatus === 'on')
                  .map((assignment) => assignment.groupName) ?? [],
              )}
            />
            <LabeledValue
              label={t('surveys.sentBy')}
              value={formatList(
                i18n.language,
                results.notificationMethods.map((method) =>
                  t(`news.send_method.${method}`),
                ),
              )}
            />
            <LabeledValue
              label={t('surveys.startDate')}
              value={
                results.publishDateTime
                  ? moment(results.publishDateTime).format('l LT')
                  : '-'
              }
            />
            <LabeledValue
              label={t('surveys.endDate')}
              value={
                results.endDateTime
                  ? moment(results.endDateTime).format('l LT')
                  : '-'
              }
            />
            <LabeledValue
              label={t('surveys.hasWebAccess.label')}
              value={survey.hasWebAccess ? t('common.yes') : t('common.no')}
            />

            {survey.rewardPoints ? (
              <LabeledValue
                label={t('surveys.rewardsEnabled.label', {
                  pointName: pointsLabel,
                })}
                value={survey.rewardPoints}
              />
            ) : null}

            {survey.type === 'training' ? (
              <>
                <LabeledValue
                  label={t('surveys.scoringEnabled.label')}
                  value={
                    survey.scoringEnabled ? t('common.yes') : t('common.no')
                  }
                />

                {survey.scoringEnabled ? (
                  <>
                    <LabeledValue
                      label={t('surveys.minScore.label')}
                      value={formatPercents(
                        i18n.language,
                        survey.minScore / 100,
                      )}
                    />
                    <LabeledValue
                      label={t('surveys.attempts.label')}
                      value={retriesToAttempts(survey.retries)}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              borderTop: getBorder(Colors.elementsGrey),

              '& > *': {
                padding: '15px 1px',
                borderBottom: getBorder(Colors.elementsGrey),

                '&:nth-of-type(2n)': {
                  paddingLeft: '40px',
                },
              },
            }}
          >
            <Typography variant="p1">{t('common.recipients')}</Typography>
            <Typography variant="p1">{results.currentRecipients}</Typography>
            <Typography variant="p1">{t('surveys.completionCount')}</Typography>
            <Typography variant="p1">{results.completedBy}</Typography>
            <Typography variant="p1">{t('surveys.responseRate')}</Typography>
            <Typography variant="p1">
              {formatPercents(
                i18n.language,
                results.currentRecipients
                  ? results.completedBy / results.currentRecipients
                  : results.completedBy
                  ? 1
                  : 0,
              )}
            </Typography>
            {['training'].includes(survey.type) ? (
              <>
                <Typography variant="p1">
                  {t('surveys.passRate.label')}
                </Typography>
                <Typography variant="p1">
                  {formatPercents(
                    i18n.language,
                    results.completedBy
                      ? results.passedBy / results.completedBy
                      : results.passedBy
                      ? 1
                      : 0,
                  )}
                </Typography>
              </>
            ) : null}
          </Box>
        </Stack>
      </Panel>

      <SurveyContentSummary
        survey={survey}
        results={results}
        surveyId={surveyId}
      />
    </Stack>
  );
};
